import { useEffect } from 'react';

interface Props {
  readonly onCachePageShow: () => void;
}

// Sometimes, the browser stores different pages in the bfcache (back/forward cache), so that when
// we navigate between the pages using the back or forward buttons, the pages are not fully reloaded,
// in order to deliver the page faster to the user. There might be some instances where we end up
// with some stale data, especially when we're changing values on the global state. E.g. we are on page 1
// which displays value x from the global state, then we go to page 2 where we change the same value x and
// then we navigate back to page 1 which is retreived from the bfcache. In this instance, page 1 will not
// show the updated value x, so we need to manually update these values (we could also disable this caching
// mechanism, but given there are very few instances where the scenario above might occur, it might be better
// to take each of these scenario one by one and also have all the caching advantages).
const useBFCachePage = ({ onCachePageShow }: Props) => {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was restored from bfcache
        onCachePageShow();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [onCachePageShow]);
};

export default useBFCachePage;
